import { SlidingNodes } from "@cmp-src/components/SlidingNodes/SlidingNodes";
import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger
} from "@cmp-src/primitives/tooltip";
import { IUserFit } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { useEffect, useRef, useState } from "react";
import { AVATAR_SIZES } from "./UserAvatar";

export type UserAvatarStackProps = {
  users: IUserFit[];
  hoverDirection?: "left" | "right";
  showRest?: boolean;
  maxVisibleAvatars?: number;
  avatarSize?: AVATAR_SIZES;
  /**
   * "floating" (default): the sliding "+N more" overlay (current behavior).
   * "badge": a static grey "+N" circle rendered in-flow after the avatars.
   */
  restVariant?: "floating" | "badge";
};

export const UserAvatarStack = ({
  users,
  hoverDirection = "left",
  showRest = true,
  maxVisibleAvatars = 3,
  avatarSize = AVATAR_SIZES.ROUNDED_8_8,
  restVariant = "floating"
}: UserAvatarStackProps) => {
  const hasExtraUsers = users.length > 3;
  const [startIndex, setStartIndex] = useState(
    hasExtraUsers ? Math.max(0, users.length - 3) : 0
  );
  const prevUsersRef = useRef<IUserFit[]>(undefined);
  const isManualNavigationRef = useRef(false);

  const intervals = useRef<Record<string, NodeJS.Timeout | undefined>>({
    loop: undefined
  });

  // Reset startIndex only when users array changes
  useEffect(() => {
    const prevUsers = prevUsersRef.current;
    prevUsersRef.current = users;

    // Skip if this is from a manual navigation or if users array is the same
    if (isManualNavigationRef.current) {
      isManualNavigationRef.current = false;
      return;
    }

    // If users changed or it's the initial render
    if (!prevUsers || prevUsers.length !== users.length) {
      if (hasExtraUsers) {
        setStartIndex(Math.max(0, users.length - 3));
      } else {
        setStartIndex(0);
      }
    }
  }, [users, hasExtraUsers]);

  // Clear intervals when component unmounts
  useEffect(() => {
    return () => {
      if (intervals.current.loop) clearInterval(intervals.current.loop);
    };
  }, []);

  const visibleUsers = hasExtraUsers
    ? users.slice(startIndex, startIndex + 3)
    : users.slice(0, 3);

  const hiddenUsersCount = users.length > 3 ? users.length - 3 : 0;

  const handleLoopNavigation = () => {
    if (users.length <= 3) return;

    isManualNavigationRef.current = true;

    // Move to previous index, or loop back to the end
    setStartIndex((prev) => {
      const newIndex = prev - 1;
      return newIndex >= 0 ? newIndex : users.length - 3;
    });
  };

  const startLoopInterval = () => {
    // Clear any existing interval
    if (intervals.current.loop) {
      clearInterval(intervals.current.loop);
      intervals.current.loop = undefined;
    }

    // Only set interval if we have more than 3 users
    if (users.length > 3) {
      setTimeout(() => {
        handleLoopNavigation();
      }, 250);
      intervals.current.loop = setInterval(() => {
        handleLoopNavigation();
      }, 1000);
    }
  };

  const stopLoopInterval = () => {
    if (intervals.current.loop) {
      clearInterval(intervals.current.loop);
      intervals.current.loop = undefined;
    }
  };

  const avatars = visibleUsers.map((user) => (
    <TooltipProvider key={user.id}>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <div>
            <UserAvatar
              user={user}
              size={"s"}
              className={cn(
                users.length > 1 ? "outline outline-2 outline-white" : ""
              )}
            />
          </div>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent>
            {user.name + (user.roles?.includes("deleted") ? " (inactive)" : "")}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  ));

  const restBadge = restVariant === "badge" &&
    users.length > maxVisibleAvatars && (
      <div className="-ml-2 relative z-0 flex h-6 w-6 items-center justify-center rounded-full bg-neutral-200 text-[10px] text-neutral-600 outline outline-2 outline-white">
        +{hiddenUsersCount}
      </div>
    );

  const restText = restVariant === "floating" &&
    showRest &&
    users.length > maxVisibleAvatars && (
      <div
        className={cn(
          "absolute top-[0.25rem] z-0 flex cursor-pointer items-center justify-center whitespace-nowrap align-center font-inter text-neutral-400 text-xs transition-all duration-300 group-hover:text-neutral-600 bg-background",
          hoverDirection === "left"
            ? "-left-5 group-hover:-left-[4.3rem]"
            : "left-5 group-hover:left-[4.3rem]"
        )}
        onClick={handleLoopNavigation}
        onMouseEnter={startLoopInterval}
        onMouseLeave={stopLoopInterval}
      >
        + {hiddenUsersCount}{" "}
        <span className="ml-1 font-inter text-white text-xs transition-all duration-300 group-hover:text-neutral-600">
          more
        </span>
      </div>
    );

  return (
    <div className="group relative flex items-center">
      {hoverDirection === "left" && restText}

      <SlidingNodes
        hoverDirection={hoverDirection}
        spacingClass="-space-x-3"
        hoverSpacing={2}
        duration={200}
        hoverToFront={true}
        className="relative z-docked transition-all duration-300"
      >
        {avatars}
      </SlidingNodes>

      {hoverDirection === "right" && restText}
      {restBadge}
    </div>
  );
};
